import type {
  ClientOrganization,
  ClientOrganizationProfile
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { ArrowRightIcon } from '@/assets';
import { ListItem } from '@/features/list/components/ListItem';
import { organizationLogoIcon } from '@/features/media/components/avatar/OrganizationLogo';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

/**
 * Accepts both the full {@link ClientOrganization} (detail/territorial endpoints) and the compact
 * {@link ClientOrganizationProfile} (own organizations endpoint), since only the shared fields
 * (id, name, logoImage, tags) are rendered.
 */
type Props = {
  organization: ClientOrganization | ClientOrganizationProfile;
  onPress: () => void;
  backgroundColor?: string;
  description?: string;
};

export const OrganizationListItem: FC<Props> = ({
  organization,
  onPress,
  backgroundColor,
  description
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.tagLabels'
  });
  const primaryTag = organization.tags?.[0];
  const parts = [
    primaryTag ? t(primaryTag) : undefined,
    'geoAreaName' in organization ? organization.geoAreaName : undefined
  ].filter(Boolean);
  const resolvedDescription =
    description ?? (parts.length > 0 ? parts.join(' · ') : undefined);

  return (
    <View
      style={[styles.wrapper, { borderColor: theme.colors.outlineVariant }]}
    >
      <ListItem
        title={organization.name ?? ''}
        description={resolvedDescription}
        icon={organizationLogoIcon(organization.logoImage)}
        arrowIcon={ArrowRightIcon}
        backgroundColor={backgroundColor ?? theme.colors.surfaceContainerLowest}
        onPress={onPress}
        hideDivider
      />
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderBottomWidth: StyleSheet.hairlineWidth,
    marginTop: -StyleSheet.hairlineWidth
  }
});
